import React, { useRef, useState } from "react";
import { Typography } from "@material-ui/core";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import { useUserContext } from "../Hooks/useUserContext";

export function RecipeImagesUpload({ config, onFilesSelected, disabled }) {
	const user = useUserContext(config);
	const inputRef = useRef(null);
	const [isProcessing, setIsProcessing] = useState(false);
	const [isDragging, setIsDragging] = useState(false);
	const [isHovering, setIsHovering] = useState(false);
	const isTouchDevice = typeof window !== "undefined" && ("ontouchstart" in window || (navigator && navigator.maxTouchPoints > 0));

	if (!user.canEditRecipe) {
		return null;
	}

	function handleClick() {
		if (disabled) return;
		if (inputRef.current) {
			inputRef.current.click();
		}
	}

	function handleDragOver(event) {
		if (disabled) return;
		event.preventDefault();
		setIsDragging(true);
	}

	function handleDragLeave(event) {
		if (disabled) return;
		event.preventDefault();
		setIsDragging(false);
		setIsHovering(false);
	}

	function handleDrop(event) {
		if (disabled) return;
		event.preventDefault();
		setIsDragging(false);
		setIsHovering(false);
		handleFilesSelected({ target: { files: event.dataTransfer.files } });
	}

	async function handleFilesSelected(event) {
		const files = Array.from(event.target.files || []);
		if (files.length === 0 || !onFilesSelected) return;
		setIsProcessing(true);
		try {
			await Promise.resolve(onFilesSelected(files));
		} finally {
			setIsProcessing(false);
		}
		if (inputRef.current) inputRef.current.value = "";
	}

	return (
		<div className="rb-image-upload">
			<input
				type="file"
				accept="image/jpeg,image/png,image/gif"
				multiple
				ref={inputRef}
				style={{ display: "none" }}
				onChange={handleFilesSelected}
			/>
			<div
				onClick={handleClick}
				onMouseEnter={() => { if (!disabled) setIsHovering(true); }}
				onMouseLeave={() => { setIsHovering(false); setIsDragging(false); }}
				onDragOver={handleDragOver}
				onDragLeave={handleDragLeave}
				onDrop={handleDrop}
				role="button"
				tabIndex={0}
				onKeyPress={(e) => {
					if (disabled) return;
					if (e.key === "Enter" || e.key === " ") handleClick();
				}}
				style={{
					border: "2px dashed #999",
					borderRadius: 8,
					padding: 12,
					display: "inline-block",
					width: "fit-content",
					textAlign: "center",
					cursor: disabled || isProcessing ? "default" : "pointer",
					opacity: disabled ? 0.4 : 1,
					backgroundColor: isDragging ? "#e8f4ff" : isHovering ? "#f8f8f8" : "transparent",
					borderColor: isDragging ? "#2196f3" : isHovering ? "#666" : "#999",
					minWidth: "auto"
				}}
			>
				<div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
					<CloudUploadIcon />
					<Typography variant="body1">
						{isProcessing ? "Adding..." : `Drag and drop images or ${isTouchDevice ? "tap" : "click"} to add`}
					</Typography>
				</div>
			</div>
		</div>
	);
}
